const PromptTemplate={
  generate: (data)=> {
    const {
      // Site Identity - NEW
      siteTitle,
      logoUrl,
      faviconUrl,
      buildNotes,

      category,
      projectType,
      complexity,
      framework,
      styling,
      features,
      customRequirements,
      context,
      constraints,

      customCategory,
      customProjectType,

      isPwa,
      hideFromSearchEngines,
      includeAdminConsole,
      adminFeatures,
      adminCredentials,
      businessInfo,

      // Design & Structure Analysis
      designConceptsUrl,
      includeSitemapAnalysis,
      includeContentStructure,

      // Additional Pages
      includePrivacyPage,
      includeTermsPage,
      includeContactPage,
      contactFormEnabled,

      // Integrations
      backendDatabase,
      hostServer,
      storageOption,

      // Output format
      outputFormat
    }=data;

    // Use custom values if 'other' is selected
    const actualCategory=category==='other' ? customCategory : category;
    const actualProjectType=projectType==='other' ? customProjectType : projectType;

    const pwaLabel=isPwa ? ' (Progressive Web App)' : '';
    const seoLabel=hideFromSearchEngines ? ' with search engine privacy' : '';
    const adminLabel=includeAdminConsole ? ' with Admin Console' : '';

    let prompt=`Create a ${complexity} level ${actualProjectType}${pwaLabel}${seoLabel}${adminLabel} using ${framework}`;
    if (styling && styling !=='vanilla-css') {
      prompt +=` with ${styling} for styling`;
    }
    prompt +=`.\n\n`;

    // Add Site Identity Section - NEW
    if (siteTitle || logoUrl || faviconUrl) {
      prompt +=`🏷️ SITE IDENTITY & BRANDING:\n`;
      if (siteTitle) {
        prompt +=`Site Title: "${siteTitle}"\n`;
        prompt +=`- Use this title consistently throughout the application\n`;
        prompt +=`- Include in page titles,navigation headers,and branding elements\n`;
        prompt +=`- Ensure proper HTML title tags and meta titles\n`;
      }
      if (logoUrl) {
        prompt +=`Logo URL: ${logoUrl}\n`;
        prompt +=`- Integrate this logo in the header/navigation\n`;
        prompt +=`- Ensure proper alt text and responsive sizing\n`;
        prompt +=`- Include hover effects and proper loading states\n`;
        prompt +=`- Use appropriate sizing for different screen sizes\n`;
      }
      if (faviconUrl) {
        prompt +=`Favicon URL: ${faviconUrl}\n`;
        prompt +=`- Set this as the site favicon in HTML head\n`;
        prompt +=`- Include proper meta tags for different icon sizes\n`;
        prompt +=`- Ensure compatibility across browsers and devices\n`;
      }
      prompt +=`\nBranding Integration Requirements:\n`;
      prompt +=`- Maintain consistent visual identity throughout the application\n`;
      prompt +=`- Ensure all branding elements are properly responsive\n`;
      prompt +=`- Include proper loading states and error handling for external assets\n`;
      prompt +=`- Add appropriate accessibility attributes (alt text,ARIA labels)\n\n`;
    }

    // Add Design & Structure Analysis Section
    if (designConceptsUrl) {
      prompt +=`🎨 DESIGN & STRUCTURE REFERENCE:\n`;
      prompt +=`Analyze and recreate the design and structure from: ${designConceptsUrl}\n\n`;
      prompt +=`Design Analysis Requirements:\n`;
      prompt +=`- Study the visual design: color palette,typography,layout structure,spacing,and visual hierarchy\n`;
      prompt +=`- Analyze component design patterns: buttons,forms,navigation,cards,and interactive elements\n`;
      prompt +=`- Recreate the overall aesthetic and user experience approach\n`;
      prompt +=`- Maintain the visual style while adapting it to ${framework} and ${styling}\n`;
      prompt +=`- Ensure responsive behavior matches or improves upon the reference\n\n`;

      // Add sitemap analysis if enabled
      if (includeSitemapAnalysis) {
        prompt +=`Sitemap & Navigation Analysis:\n`;
        prompt +=`- Study the website's navigation structure and menu organization\n`;
        prompt +=`- Analyze page hierarchy and information architecture\n`;
        prompt +=`- Recreate the navigation flow and user journey patterns\n`;
        prompt +=`- Implement similar URL structure and routing logic\n`;
        prompt +=`- Mirror the breadcrumb and navigation patterns\n\n`;
      }

      // Add content structure analysis if enabled
      if (includeContentStructure) {
        prompt +=`Content Structure Analysis:\n`;
        prompt +=`- Study how content is organized and presented on each page\n`;
        prompt +=`- Analyze section layouts,content blocks,and information grouping\n`;
        prompt +=`- Recreate the content hierarchy and information flow\n`;
        prompt +=`- Implement similar content management and organization patterns\n`;
        prompt +=`- Mirror the content presentation and formatting approach\n\n`;
      }

      prompt +=`Integration Instructions:\n`;
      prompt +=`- The final application should feel familiar to users of the reference site\n`;
      prompt +=`- Maintain design consistency while adding the requested new features\n`;
      prompt +=`- Ensure the new functionality integrates seamlessly with the reference design language\n`;
      prompt +=`- Optimize and improve upon the reference design where possible\n\n`;
    }


    // Add PWA-specific context
    if (isPwa) {
      prompt +=`This is a Progressive Web App (PWA) that should provide a native app-like experience with modern web capabilities. The app should be installable,work offline,and provide push notifications. `;
    }

    // Add search engine privacy context
    if (hideFromSearchEngines) {
      prompt +=`This application should be hidden from search engines and designed for private/internal use. Include proper noindex meta tags,robots.txt configuration,and privacy-focused design. `;
    }

    // Add admin console context
    if (includeAdminConsole) {
      prompt +=`This application includes a comprehensive admin console for managing the application. The admin panel should be secure,user-friendly,and provide all necessary management capabilities. `;
      // Add admin credentials context
      if (adminCredentials.useCustomCredentials && adminCredentials.username && adminCredentials.password) {
        prompt +=`The admin login should use the following credentials: Username: "${adminCredentials.username}",Password: "${adminCredentials.password}". `;
      }
    }

    // Add category-specific context
    if (category==='other') {
      prompt +=`This is a ${actualCategory} project that should be well-structured and follow best practices. `;
    } else {
      switch (category) {
        case 'web-development':
          prompt +=`This is a web development project that should follow modern best practices and be production-ready. `;
          break;
        case 'mobile-app':
          prompt +=`This is a mobile application that should be responsive and touch-friendly. `;
          break;
        case 'data-analysis':
          prompt +=`This is a data analysis project that should handle data efficiently and provide clear visualizations. `;
          break;
        case 'automation':
          prompt +=`This is an automation project that should be reliable and handle edge cases gracefully. `;
          break;
        case 'ui-design':
          prompt +=`This is a UI design project that should be visually appealing and user-friendly. `;
          break;
        case 'api-integration':
          prompt +=`This is an API integration project that should handle API calls efficiently and manage errors properly. `;
          break;
        case 'e-commerce':
          prompt +=`This is an e-commerce project that should be secure,scalable,and provide excellent user experience. `;
          break;
        case 'content-management':
          prompt +=`This is a content management project that should be intuitive and support content creators. `;
          break;
        case 'entertainment':
          prompt +=`This is an entertainment project that should be engaging and provide smooth user interactions. `;
          break;
        case 'productivity':
          prompt +=`This is a productivity project that should be efficient and help users accomplish their tasks. `;
          break;
        case 'health-fitness':
          prompt +=`This is a health and fitness project that should be motivating and track user progress effectively. `;
          break;
        case 'education':
          prompt +=`This is an educational project that should be engaging and facilitate effective learning. `;
          break;
        case 'finance':
          prompt +=`This is a finance project that should be secure,accurate,and provide clear financial insights. `;
          break;
        case 'real-estate':
          prompt +=`This is a real estate project that should be informative and help users make property decisions. `;
          break;
        case 'communication':
          prompt +=`This is a communication project that should facilitate seamless interaction between users. `;
          break;
        case 'calendar-events':
          prompt +=`This is a calendar and events project that should help users organize and manage their time effectively. `;
          break;
        case 'travel':
          prompt +=`This is a travel project that should be helpful for planning and managing travel experiences. `;
          break;
        case 'gaming':
          prompt +=`This is a gaming project that should be fun,engaging,and provide smooth gameplay. `;
          break;
        case 'media-photography':
          prompt +=`This is a media and photography project that should showcase content beautifully and efficiently. `;
          break;
        case 'security':
          prompt +=`This is a security project that should prioritize user privacy and data protection. `;
          break;
        case 'cloud-storage':
          prompt +=`This is a cloud storage project that should be reliable and provide easy file management. `;
          break;
        case 'monitoring':
          prompt +=`This is a monitoring project that should provide real-time insights and alerting capabilities. `;
          break;
      }
    }

    // Add features if they exist
    if (features && features.length > 0) {
      prompt +=`\n\nKey features to include:\n`;
      features.forEach(feature=> {
        switch (feature) {
          case 'responsive':
            prompt +=`- Fully responsive design that works on all devices\n`;
            break;
          case 'animations':
            prompt +=`- Smooth animations and transitions using Framer Motion\n`;
            break;
          case 'dark-mode':
            prompt +=`- Dark mode toggle functionality\n`;
            break;
          case 'auth':
            prompt +=`- User authentication system\n`;
            break;
          case 'database':
            prompt +=`- Database integration for data persistence\n`;
            break;
          case 'api':
            prompt +=`- API integration for external data\n`;
            break;
          case 'search':
            prompt +=`- Search functionality with filtering\n`;
            break;
          case 'filters':
            prompt +=`- Advanced filtering and sorting options\n`;
            break;
          case 'service-worker':
            prompt +=`- Service worker for offline functionality and caching\n`;
            break;
          case 'app-shell':
            prompt +=`- App shell architecture for fast loading\n`;
            break;
          case 'installable':
            prompt +=`- Web app manifest for installable experience\n`;
            break;
          case 'push-notifications':
            prompt +=`- Push notification system\n`;
            break;
          case 'offline-support':
            prompt +=`- Offline functionality with data synchronization\n`;
            break;
          case 'background-sync':
            prompt +=`- Background sync for offline data synchronization\n`;
            break;
          default:
            // Convert feature name to readable format
            const featureName=feature.replace('-',' ').replace(/\b\w/g,l=> l.toUpperCase());
            prompt +=`- ${featureName}\n`;
            break;
        }
      });
    }

    // Add Additional Pages Section
    const additionalPages=[];
    if (includePrivacyPage) additionalPages.push('Privacy Policy');
    if (includeTermsPage) additionalPages.push('Terms of Service');
    if (includeContactPage) additionalPages.push('Contact');

    if (additionalPages.length > 0) {
      prompt +=`\n\nAdditional Pages to Include:\n`;

      if (includePrivacyPage) {
        prompt +=`- Privacy Policy Page:\n`;
        prompt +=`  * Create a comprehensive privacy policy page with customizable content\n`;
        prompt +=`  * Include sections for data collection,usage,cookies,and user rights\n`;
        prompt +=`  * Make the content easily editable and legally compliant\n`;
        prompt +=`  * Add proper navigation and footer links to this page\n`;
      }

      if (includeTermsPage) {
        prompt +=`- Terms of Service Page:\n`;
        prompt +=`  * Create a detailed terms of service page with legal terms and conditions\n`;
        prompt +=`  * Include sections for service usage,user responsibilities,and limitations\n`;
        prompt +=`  * Ensure the content is customizable for different business types\n`;
        prompt +=`  * Add proper legal disclaimers and effective date functionality\n`;
      }

      if (includeContactPage) {
        prompt +=`- Contact Page:\n`;
        prompt +=`  * Create a comprehensive contact page with multiple contact methods\n`;
        prompt +=`  * Include contact form with validation and submission handling\n`;
        if (businessInfo && (businessInfo.companyName || businessInfo.businessEmail || businessInfo.phoneNumber)) {
          prompt +=`  * Display business information:\n`;
          if (businessInfo.companyName) prompt +=`    - Company: ${businessInfo.companyName}\n`;
          if (businessInfo.businessEmail) prompt +=`    - Email: ${businessInfo.businessEmail}\n`;
          if (businessInfo.phoneNumber) prompt +=`    - Phone: ${businessInfo.phoneNumber}\n`;
          if (businessInfo.addressLine1) {
            prompt +=`    - Address: ${businessInfo.addressLine1}`;
            if (businessInfo.addressLine2) prompt +=`,${businessInfo.addressLine2}`;
            if (businessInfo.city) prompt +=`,${businessInfo.city}`;
            if (businessInfo.stateProvince) prompt +=`,${businessInfo.stateProvince}`;
            if (businessInfo.postalCode) prompt +=` ${businessInfo.postalCode}`;
            if (businessInfo.country) prompt +=`,${businessInfo.country}`;
            prompt +=`\n`;
          }
        }
        prompt +=`  * Add map integration if business address is provided\n`;
        prompt +=`  * Include social media links and operating hours if available\n`;
        prompt +=`  * Implement proper form handling and email notifications\n`;
      }

      prompt +=`\nPage Integration Requirements:\n`;
      prompt +=`- Add proper navigation links in header/footer for all additional pages\n`;
      prompt +=`- Ensure consistent design and branding across all pages\n`;
      prompt +=`- Implement proper routing and URL structure\n`;
      prompt +=`- Add meta tags and SEO optimization for each page\n`;
      prompt +=`- Ensure mobile responsiveness for all additional pages\n`;
    }

    // Add admin console features if enabled
    if (includeAdminConsole && adminFeatures) {
      prompt +=`\n\nAdmin Console Features:\n`;

      // Core admin features
      if (adminFeatures.contentManagement) {
        prompt +=`- Content Management System: Create,edit,delete,and organize all website content\n`;
      }
      if (adminFeatures.userManagement) {
        prompt +=`- User Management: Manage user accounts,roles,and permissions\n`;
      }
      if (adminFeatures.analytics) {
        prompt +=`- Analytics & Reports: Track website performance,user engagement,and generate reports\n`;
      }
      if (adminFeatures.mediaStorage) {
        prompt +=`- Media Storage: Upload,organize,and manage images,videos,and files with cloud storage integration\n`;
      }
      if (adminFeatures.customBranding) {
        prompt +=`- Custom Branding: Upload custom logo,customize colors,fonts,and brand text throughout the application\n`;
      }
      if (adminFeatures.seoManagement && !hideFromSearchEngines) {
        prompt +=`- SEO Management: Manage meta tags,sitemaps,schema markup,and SEO settings\n`;
      }

      // Business features
      if (adminFeatures.businessInfo) {
        prompt +=`- Business Information Management: Manage business address,contact details,email,phone,website,and social media links\n`;
      }
      if (adminFeatures.operatingHours) {
        prompt +=`- Operating Hours Management: Set and manage business hours,special hours,holidays,and availability\n`;
      }
      if (adminFeatures.onlineBooking) {
        prompt +=`- Online Booking System: Manage appointments,services,staff schedules,and customer bookings\n`;
      }
      if (adminFeatures.menuManagement) {
        prompt +=`- Menu & Pricing Management: Manage menu items,categories,prices,specials,and availability\n`;
      }

      // Advanced features
      if (adminFeatures.rolePermissions) {
        prompt +=`- Role & Permissions: Advanced user roles with granular permissions and access control\n`;
      }
      if (adminFeatures.backupRestore) {
        prompt +=`- Backup & Restore: Automated backups and system restore functionality\n`;
      }
      if (adminFeatures.apiManagement) {
        prompt +=`- API Management: Manage API keys,webhooks,and external integrations\n`;
      }
      if (adminFeatures.customFields) {
        prompt +=`- Custom Fields: Create and manage custom data fields and forms\n`;
      }
    }

    // Add custom requirements
    if (customRequirements) {
      prompt +=`\n\nSpecific requirements:\n${customRequirements}\n`;
    }

    // Add build notes (internal use,not for AI)
    if (buildNotes) {
      // This part is for saving but won't be in the prompt sent to the AI
    }

    // Add context if provided
    if (context) {
      prompt +=`\n\nProject Context:\n${context}\n`;
    }

    // Add constraints if provided
    if (constraints) {
      prompt +=`\n\nProject Constraints:\n${constraints}\n`;
    }

    // Add PWA-specific technical requirements
    if (isPwa) {
      prompt +=`\n\nPWA-Specific Requirements:\n`;
      prompt +=`- Create a comprehensive web app manifest (manifest.json)\n`;
      prompt +=`- Implement service worker for offline functionality\n`;
      prompt +=`- Add app shell architecture for fast loading\n`;
      prompt +=`- Ensure the app is installable on devices\n`;
      prompt +=`- Implement push notification system\n`;
      prompt +=`- Add offline page and caching strategies\n`;
      prompt +=`- Ensure proper PWA icons and splash screens\n`;
      prompt +=`- Implement background sync for data synchronization\n`;
      prompt +=`- Add installation prompt component for better UX\n`;
      prompt +=`- Ensure proper PWA compliance and best practices\n`;
    }

    // Add search engine privacy requirements
    if (hideFromSearchEngines) {
      prompt +=`\n\nSearch Engine Privacy Requirements:\n`;
      prompt +=`- Add noindex meta tags to prevent search engine indexing\n`;
      prompt +=`- Create robots.txt file to block search engine crawlers\n`;
      prompt +=`- Include "noindex,nofollow,noarchive,nosnippet" meta directives\n`;
      prompt +=`- Add X-Robots-Tag headers for additional protection\n`;
      prompt +=`- Remove or disable any SEO optimization features\n`;
      prompt +=`- Design for private/internal access rather than public discovery\n`;
      prompt +=`- Consider adding basic authentication or access controls\n`;
      prompt +=`- Ensure no sitemaps are generated or accessible\n`;
      prompt +=`- Disable social media sharing meta tags (Open Graph,Twitter Cards)\n`;
      prompt +=`- Add privacy-focused design elements and messaging\n`;
    }

    // Add admin console technical requirements
    if (includeAdminConsole) {
      prompt +=`\n\nAdmin Console Technical Requirements:\n`;
      prompt +=`- Create secure admin authentication and session management\n`;
      // Add custom credentials if specified
      if (adminCredentials.useCustomCredentials && adminCredentials.username && adminCredentials.password) {
        prompt +=`- Implement admin login with username "${adminCredentials.username}" and password "${adminCredentials.password}"\n`;
        prompt +=`- Add password hashing and secure credential validation\n`;
        prompt +=`- Include option to change admin credentials from within the admin panel\n`;
      } else {
        prompt +=`- Implement default admin authentication (admin/admin123 or similar)\n`;
      }
      prompt +=`- Implement role-based access control (RBAC)\n`;
      prompt +=`- Design responsive admin dashboard with navigation\n`;
      prompt +=`- Add data tables with sorting,filtering,and pagination\n`;
      prompt +=`- Implement CRUD operations for all managed entities\n`;
      prompt +=`- Add form validation and error handling\n`;
      prompt +=`- Include audit logs and activity tracking\n`;
      prompt +=`- Implement secure file upload and media management\n`;
      prompt +=`- Add data export functionality (CSV,PDF)\n`;
      prompt +=`- Include system settings and configuration management\n`;
      prompt +=`- Add backup and restore functionality\n`;
      prompt +=`- Implement API rate limiting and security measures\n`;
      prompt +=`- Create admin-specific components and layouts\n`;
      prompt +=`- Add comprehensive admin documentation\n`;

      // Business-specific admin requirements
      if (adminFeatures.businessInfo || adminFeatures.operatingHours) {
        prompt +=`- Create business profile management interface\n`;
        prompt +=`- Add contact information and social media management\n`;
        prompt +=`- Implement operating hours scheduler with special dates\n`;
      }
      if (adminFeatures.onlineBooking) {
        prompt +=`- Create appointment booking management system\n`;
        prompt +=`- Add service and staff management interfaces\n`;
        prompt +=`- Implement calendar integration and availability management\n`;
        prompt +=`- Add customer management and booking history\n`;
        prompt +=`- Include automated email/SMS notifications\n`;
      }
      if (adminFeatures.menuManagement) {
        prompt +=`- Create menu item management interface\n`;
        prompt +=`- Add category and pricing management\n`;
        prompt +=`- Implement special offers and promotions system\n`;
        prompt +=`- Add inventory tracking and availability management\n`;
        prompt +=`- Include menu display customization options\n`;
      }
    }

    // Add business information context if available
    if (businessInfo && (includeContactPage || adminFeatures.businessInfo)) {
      prompt +=`\n\nBusiness Information Integration:\n`;
      if (businessInfo.companyName) {
        prompt +=`- Company Name: ${businessInfo.companyName}\n`;
      }
      if (businessInfo.businessEmail) {
        prompt +=`- Business Email: ${businessInfo.businessEmail}\n`;
      }
      if (businessInfo.phoneNumber) {
        prompt +=`- Phone Number: ${businessInfo.phoneNumber}\n`;
      }
      if (businessInfo.websiteUrl) {
        prompt +=`- Website URL: ${businessInfo.websiteUrl}\n`;
      }
      if (businessInfo.addressLine1) {
        prompt +=`- Address: ${businessInfo.addressLine1}`;
        if (businessInfo.addressLine2) prompt +=`,${businessInfo.addressLine2}`;
        if (businessInfo.city) prompt +=`,${businessInfo.city}`;
        if (businessInfo.stateProvince) prompt +=`,${businessInfo.stateProvince}`;
        if (businessInfo.postalCode) prompt +=` ${businessInfo.postalCode}`;
        if (businessInfo.country) prompt +=`,${businessInfo.country}`;
        prompt +=`\n`;
      }
      // Social media links
      const socialLinks=[];
      if (businessInfo.socialFacebook) socialLinks.push(`Facebook: ${businessInfo.socialFacebook}`);
      if (businessInfo.socialTwitter) socialLinks.push(`Twitter: ${businessInfo.socialTwitter}`);
      if (businessInfo.socialInstagram) socialLinks.push(`Instagram: ${businessInfo.socialInstagram}`);
      if (businessInfo.socialLinkedin) socialLinks.push(`LinkedIn: ${businessInfo.socialLinkedin}`);
      if (businessInfo.socialYoutube) socialLinks.push(`YouTube: ${businessInfo.socialYoutube}`);
      if (socialLinks.length > 0) {
        prompt +=`- Social Media: ${socialLinks.join(',')}\n`;
      }
      prompt +=`\nEnsure all business information is consistently displayed across the application,contact pages,and admin panels.\n`;
    }


    // Add integration requirements
    prompt +=`\n\nIntegration Requirements:\n`;

    // Backend Database
    if (backendDatabase==='supabase') {
      prompt +=`- Backend Database: Supabase\n`;
      prompt +=`  * Implement Supabase for database management\n`;
      prompt +=`  * Use Supabase authentication for user management\n`;
      prompt +=`  * Set up real-time subscriptions for live data updates\n`;
      prompt +=`  * Configure proper row-level security (RLS) policies\n`;
      prompt +=`  * Implement Supabase storage for file management if needed\n`;
    } else {
      prompt +=`- Backend Database: No Code Backend\n`;
      prompt +=`  * Provide setup instructions for a no-code backend solution\n`;
      prompt +=`  * Include configuration for backend services like Firebase,Appwrite,or similar\n`;
      prompt +=`  * Add instructions for connecting frontend to backend API\n`;
    }

    // Host Server
    if (hostServer==='netlify') {
      prompt +=`- Host Server: Netlify\n`;
      prompt +=`  * Optimize for Netlify deployment\n`;
      prompt +=`  * Include netlify.toml configuration if needed\n`;
      prompt +=`  * Add Netlify-specific environment variables setup\n`;
      prompt +=`  * Implement Netlify functions if required\n`;
    } else {
      prompt +=`- Host Server: Cloudflare Pages\n`;
      prompt +=`  * Optimize for Cloudflare Pages deployment\n`;
      prompt +=`  * Include wrangler.json configuration\n`;
      prompt +=`  * Add Cloudflare-specific environment variables setup\n`;
      prompt +=`  * Implement Cloudflare Workers if required\n`;
    }

    // Storage Option
    switch (storageOption) {
      case 'netlify-blobs':
        prompt +=`- Storage Option: Netlify Blobs\n`;
        prompt +=`  * Implement Netlify Blob storage for file management\n`;
        prompt +=`  * Include setup instructions for Netlify Blob Store\n`;
        prompt +=`  * Add configuration for file upload and retrieval\n`;
        break;
      case 'google-drive':
        prompt +=`- Storage Option: Google Drive\n`;
        prompt +=`  * Implement Google Drive API integration\n`;
        prompt +=`  * Include OAuth2 authentication flow\n`;
        prompt +=`  * Add file management functionality using Google Drive\n`;
        break;
      case 'aws':
        prompt +=`- Storage Option: Amazon AWS\n`;
        prompt +=`  * Implement AWS S3 for file storage\n`;
        prompt +=`  * Include AWS SDK integration\n`;
        prompt +=`  * Add configuration for S3 bucket management\n`;
        break;
      default: // Dropbox
        prompt +=`- Storage Option: Dropbox\n`;
        prompt +=`  * Implement Dropbox API integration\n`;
        prompt +=`  * Include OAuth2 authentication flow\n`;
        prompt +=`  * Add file management functionality using Dropbox\n`;
        break;
    }


    // Add technical constraints
    prompt +=`\n\nTechnical Requirements:\n`;
    prompt +=`- Use ${framework} as the main framework\n`;
    prompt +=`- Implement ${styling} for styling\n`;
    prompt +=`- Ensure code is clean,well-commented,and follows best practices\n`;
    prompt +=`- Make the application responsive and accessible\n`;
    prompt +=`- Include proper error handling and loading states\n`;

    // Add Site Identity technical requirements - NEW
    if (siteTitle || logoUrl || faviconUrl) {
      prompt +=`\nSite Identity Technical Integration:\n`;
      if (siteTitle) {
        prompt +=`- Set "${siteTitle}" as the main site title throughout the application\n`;
        prompt +=`- Include proper HTML title tags: <title>${siteTitle}</title>\n`;
        prompt +=`- Add meta tags for social sharing with the site title\n`;
        prompt +=`- Update navigation and header components to display the title\n`;
      }
      if (logoUrl) {
        prompt +=`- Integrate logo from ${logoUrl} in header/navigation components\n`;
        prompt +=`- Add proper alt text: alt="${siteTitle ? siteTitle + ' Logo' : 'Logo'}"\n`;
        prompt +=`- Implement responsive sizing for different screen sizes\n`;
        prompt +=`- Include loading states and error handling for logo loading\n`;
        prompt +=`- Add hover effects and proper accessibility attributes\n`;
      }
      if (faviconUrl) {
        prompt +=`- Set favicon using ${faviconUrl} in HTML head section\n`;
        prompt +=`- Include multiple icon sizes for different devices\n`;
        prompt +=`- Add Apple touch icons and other device-specific icons\n`;
        prompt +=`- Ensure proper meta tags for favicon compatibility\n`;
      }
    }


    // Add output format specification
    if (outputFormat) {
      prompt +=`- Output format: ${outputFormat}\n`;
    }

    // Add search engine specific constraints
    if (hideFromSearchEngines) {
      prompt +=`- Implement search engine blocking mechanisms\n`;
      prompt +=`- Focus on internal usability rather than public SEO\n`;
      prompt +=`- Add privacy-focused navigation and user flows\n`;
    }

    // Add admin console specific constraints
    if (includeAdminConsole) {
      prompt +=`- Implement secure admin authentication and authorization\n`;
      prompt +=`- Create intuitive admin user interface with proper navigation\n`;
      prompt +=`- Add comprehensive data validation and security measures\n`;
      prompt +=`- Include proper admin-specific routing and access control\n`;
      if (adminCredentials.useCustomCredentials && adminCredentials.username && adminCredentials.password) {
        prompt +=`- Ensure admin credentials are securely stored and properly validated\n`;
        prompt +=`- Add password change functionality within the admin panel\n`;
        prompt +=`- Implement session management and timeout for admin users\n`;
      }
    }

    // Add Greta-specific instructions
    prompt +=`\n\nGreta Instructions:\n`;
    prompt +=`- Create a complete,production-ready application\n`;
    prompt +=`- Provide all necessary files and folder structure\n`;
    prompt +=`- Include package.json with all required dependencies\n`;
    prompt +=`- Ensure all imports and exports are correct\n`;
    prompt +=`- Add proper TypeScript types if using TypeScript\n`;
    prompt +=`- Include comprehensive README with setup instructions\n`;

    // Add design reference specific instructions
    if (designConceptsUrl) {
      prompt +=`- Carefully analyze the reference URL design and recreate its visual appeal\n`;
      prompt +=`- Maintain design consistency while implementing new functionality\n`;
      prompt +=`- Ensure the final result feels familiar to users of the reference site\n`;
      prompt +=`- Document any design decisions or adaptations made\n`;
    }

    // Add PWA-specific files
    if (isPwa) {
      prompt +=`- Include manifest.json with proper PWA configuration\n`;
      prompt +=`- Create service worker file with caching strategies\n`;
      prompt +=`- Add offline.html page for offline functionality\n`;
      prompt +=`- Include PWA icons in various sizes (192x192,512x512,etc.)\n`;
      prompt +=`- Add installation prompt component\n`;
      prompt +=`- Implement PWA update notification system\n`;
      prompt +=`- Add proper meta tags for PWA support\n`;
    }

    // Add search engine privacy files
    if (hideFromSearchEngines) {
      prompt +=`- Create robots.txt file with comprehensive blocking rules\n`;
      prompt +=`- Add appropriate meta tags in HTML head section\n`;
      prompt +=`- Include privacy policy or access notice if needed\n`;
      prompt +=`- Ensure no sitemap.xml or search-friendly URLs\n`;
    }

    // Add admin console files
    if (includeAdminConsole) {
      prompt +=`- Create admin dashboard components and layouts\n`;
      prompt +=`- Add admin authentication and authorization components\n`;
      prompt +=`- Include admin-specific routing and navigation\n`;
      prompt +=`- Create data management interfaces and forms\n`;
      prompt +=`- Add admin API endpoints and database schemas\n`;
      prompt +=`- Include admin documentation and user guides\n`;
      if (adminCredentials.useCustomCredentials && adminCredentials.username && adminCredentials.password) {
        prompt +=`- Create secure credential storage and validation system\n`;
        prompt +=`- Add admin login form with the specified credentials\n`;
        prompt +=`- Include password change functionality in admin settings\n`;
      }
    }

    // Add additional page files
    if (additionalPages.length > 0) {
      prompt +=`- Create dedicated page components for: ${additionalPages.join(',')}\n`;
      prompt +=`- Add proper routing for all additional pages\n`;
      prompt +=`- Include navigation links and footer references\n`;
      prompt +=`- Ensure consistent styling and branding across pages\n`;
    }

    // Add complexity-specific instructions
    switch (complexity) {
      case 'beginner':
        prompt +=`- Keep the code simple and well-documented\n`;
        prompt +=`- Focus on core functionality over advanced features\n`;
        break;
      case 'intermediate':
        prompt +=`- Include intermediate features like state management\n`;
        prompt +=`- Add some advanced UI components and interactions\n`;
        break;
      case 'advanced':
        prompt +=`- Implement advanced patterns and optimizations\n`;
        prompt +=`- Include complex state management and advanced features\n`;
        prompt +=`- Add performance optimizations and advanced error handling\n`;
        break;
    }

    // Add special note for custom projects
    if (category==='other' || projectType==='other') {
      prompt +=`\n\nNote: This is a custom project type. Please pay special attention to the specific requirements provided and ensure the implementation meets the unique needs of this project.\n`;
    }

    // Add design reference note
    if (designConceptsUrl) {
      prompt +=`\n\nDesign Reference Note: This application should recreate the visual design and user experience from ${designConceptsUrl}. Study the reference carefully and ensure the final result captures the essence of the original design while implementing the requested functionality. Pay special attention to color schemes,typography,layout patterns,and user interaction flows.\n`;
    }

    // Add PWA note
    if (isPwa) {
      prompt +=`\n\nPWA Note: This Progressive Web App should provide a native app-like experience while being accessible through web browsers. Focus on performance,offline functionality,user engagement features,and seamless installation experience. Ensure compliance with PWA standards and best practices.\n`;
    }

    // Add search engine privacy note
    if (hideFromSearchEngines) {
      prompt +=`\n\nPrivacy Note: This application is designed to be hidden from search engines and should not appear in search results. Focus on internal usability,private access patterns,and user privacy. Implement comprehensive search engine blocking while maintaining excellent user experience for authorized users.\n`;
    }

    // Add admin console note
    if (includeAdminConsole) {
      prompt +=`\n\nAdmin Console Note: This application includes a comprehensive admin console for managing all aspects of the application. The admin panel should be secure,intuitive,and provide all necessary tools for content management,user management,and business operations. Focus on creating a professional admin experience with proper security measures,user-friendly interfaces,and efficient workflows.\n`;
      // Add custom credentials note
      if (adminCredentials.useCustomCredentials && adminCredentials.username && adminCredentials.password) {
        prompt +=`\n\nAdmin Credentials Note: The admin console should use the specified credentials (${adminCredentials.username}/${adminCredentials.password}) for login. Ensure these credentials are properly hashed and stored securely. Include functionality for the admin to change these credentials from within the admin panel. Add proper session management and security measures for admin authentication.\n`;
      }
      // Add business-specific notes
      if (adminFeatures.businessInfo || adminFeatures.operatingHours || adminFeatures.onlineBooking || adminFeatures.menuManagement) {
        prompt +=`\n\nBusiness Features Note: This application includes business-specific admin features. Ensure that business information,operating hours,booking systems,and menu management are intuitive and comprehensive. Focus on creating tools that help business owners manage their operations effectively.\n`;
      }
    }

    // Add additional pages note
    if (additionalPages.length > 0) {
      prompt +=`\n\nAdditional Pages Note: This application includes ${additionalPages.join(',')} page(s). Ensure these pages are properly integrated with the main application,have consistent design and navigation,and provide all necessary functionality. The content should be easily customizable and legally compliant where applicable.\n`;
    }

    // Add Site Identity note - NEW
    if (siteTitle || logoUrl || faviconUrl) {
      prompt +=`\n\nSite Identity Note: This application has specific branding requirements. `;
      if (siteTitle) {
        prompt +=`The site title "${siteTitle}" should be prominently displayed and consistently used throughout the application. `;
      }
      if (logoUrl) {
        prompt +=`The logo from ${logoUrl} should be properly integrated with responsive sizing and loading states. `;
      }
      if (faviconUrl) {
        prompt +=`The favicon from ${faviconUrl} should be properly configured for all devices and browsers. `;
      }
      prompt +=`Ensure all branding elements work together cohesively and maintain visual consistency across the entire application.\n`;
    }

    // Add Integrations note
    prompt +=`\n\nIntegrations Note: This application requires specific integration configurations. `;
    prompt +=`The backend database should be ${backendDatabase==='supabase' ? 'Supabase' : 'a no-code solution'}. `;
    prompt +=`The hosting platform should be ${hostServer}. `;
    prompt +=`The file storage solution should be ${storageOption}. `;
    prompt +=`Ensure all integrations are properly configured with appropriate authentication,environment variables,and deployment settings.\n`;

    return prompt;
  }
};

export default PromptTemplate;